import React from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { t } from '@/translations';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { motion } from 'framer-motion';

const FAQ = () => {
  const { language } = useLanguage();

  const faqs = [
    {
      question: t('faq.q1', language),
      answer: t('faq.a1', language)
    },
    {
      question: t('faq.q2', language),
      answer: t('faq.a2', language)
    },
    {
      question: t('faq.q3', language),
      answer: t('faq.a3', language)
    },
    {
      question: t('faq.q4', language),
      answer: t('faq.a4', language)
    },
    {
      question: t('faq.q5', language),
      answer: t('faq.a5', language)
    },
    {
      question: t('faq.q6', language),
      answer: t('faq.a6', language)
    },
    {
      question: t('faq.q7', language),
      answer: t('faq.a7', language)
    },
    {
      question: t('faq.q8', language),
      answer: t('faq.a8', language)
    }
  ];

  return (
    <div data-testid="faq-page" className="pt-32">
      <section className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <p className="text-xs tracking-[0.2em] uppercase text-primary font-semibold mb-4">
            {language === 'it' ? 'Domande' : 'Questions'}
          </p>
          <h1
            className="text-5xl sm:text-6xl font-light tracking-tight leading-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="page-title"
          >
            {t('faq.title', language)}
          </h1>
          <p className="text-base leading-relaxed text-muted-foreground">
            {language === 'it'
              ? 'Trova le risposte alle domande più comuni. Se non trovi quello che cerchi, contattami direttamente.'
              : 'Find answers to the most common questions. If you don\'t find what you\'re looking for, contact me directly.'}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger
                  data-testid={`faq-question-${index}`}
                  className="text-left"
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent data-testid={`faq-answer-${index}`}>
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </section>

      <section className="bg-secondary/30 py-24 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2
              className="text-3xl sm:text-4xl font-light tracking-tight leading-tight mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {language === 'it' ? 'Hai altre domande?' : 'Have more questions?'}
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground mb-8">
              {language === 'it'
                ? 'Non esitare a contattarmi. Risponderò con piacere a tutte le tue curiosità.'
                : 'Don\'t hesitate to contact me. I will be happy to answer all your questions.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="mailto:info@matteogiacomettiph.it"
                data-testid="email-link"
                className="text-primary hover:underline"
              >
                info@matteogiacomettiph.it
              </a>
              <span className="hidden sm:inline text-muted-foreground">|</span>
              <a
                href="https://wa.me/393484512917"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-link"
                className="text-primary hover:underline"
              >
                WhatsApp: +39 348 451 2917
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
